/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import dayjs from "dayjs";
import { Else, If, Then } from "react-if";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotEmpty } from "~/tools";
import { useGetIncomingEvents } from "../hooks/useGetIncomingEvents/useGetIncomingEvents";
import {
  EventsGridHeader,
  EventsTableHeaderRow,
  EventsTableInner,
  EventsTableScrollContainer,
} from "../styled";
import { QueryGetEvents } from "./constants";
import { prepareIncomingEventsFilterBy } from "./eventFilterBuilders";
import { EventsLoader } from "./EventLoader";
import { IncomingEventItem } from "./IncomingEventItem";

type Props = {
  onResultsCountChange?: (count: number) => void;
};

export function IncomingEventsTable({ onResultsCountChange }: Props) {
  const { queryState } = useQueryState<QueryGetEvents>();

  const { data, loading, count } = useGetIncomingEvents({
    page: queryState.page ?? "1",
    limit: queryState.limit,
    sort: {
      field: queryState.sortField,
      order: queryState.order,
    },
    filter: {
      filterBy: prepareIncomingEventsFilterBy(queryState),
      phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
        ? queryState.searchPhrase
        : undefined,
    },
  });

  const wrongRangeInput = dayjs(queryState.startDateUTC).isAfter(
    queryState.endDateUTC
  );

  useEffect(() => {
    onResultsCountChange?.(count);
  }, [count, onResultsCountChange]);

  return (
    <>
      <LoaderWrap
        loading={loading}
        Component={() => (
          <ListLoader>
            <EventsLoader />
          </ListLoader>
        )}
      >
        <If condition={isEmpty(data)}>
          <Then>
            <If condition={wrongRangeInput}>
              <Then>
                <NoData
                  hasIcon
                  message={
                    "The date range is incorrect. Please ensure that the 'startDate' and 'endDate' parameters in the link are correctly specified."
                  }
                />
              </Then>
              <Else>
                <NoData hasIcon message={"There are no Events."} />
              </Else>
            </If>
          </Then>
          <Else>
            <>
              <EventsTableScrollContainer>
                <EventsTableInner>
                  <EventsTableHeaderRow>
                    <EventsGridHeader hasPadding>
                      <div />
                      <div />
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Event Name
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Start Time (local time)
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        End Time (local time)
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Event Duration
                      </Text>
                      <div />
                    </EventsGridHeader>
                  </EventsTableHeaderRow>
                  {data.map((event) => (
                    <IncomingEventItem key={event.id} event={event} />
                  ))}
                </EventsTableInner>
              </EventsTableScrollContainer>
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
